import trim from 'lodash/trim';
import uniq from 'lodash/uniq';
import get from 'lodash/get';
import { mapState } from 'vuex';
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import { extractErrorMessage, downloadDataAsFile, fileNameParser, confirmBeforeDialogClose } from '@/libs';
import itemWithKeywordListItem from '@/components/KeywordsManagement/ItemWithKeywordListItem.vue';
import itemWithKeywordNew from '@/components/KeywordsManagement/ItemWithKeywordNew.vue';
import { PAGINATION_DEFAULT } from '@/constants/pagination';

const CHECK_SCROLLBAR_TIMEOUT_MS = 500;
const SEARCH_PAGE_SIZE = 1000;
const UNIQUE_CONSTRAINT_CODE = 406;
const NO_KEYWORD_CODE = 422;
const PROGRESS_BAR_WAIT_MS = 1000;

const prepareItem = item => ({
  ...item,
  name: trim(item.name),
});

export default {
  components: {
    ManagementPage,
    itemWithKeywordListItem,
    itemWithKeywordNew,
  },
  data() {
    return {
      addItemInput: false,
      searchPageSize: SEARCH_PAGE_SIZE,
      scrollbar: true,
      scrollbarTimeout: null,
      tempItem: {},
      pagination: PAGINATION_DEFAULT,
      isExportDialogVisible: false,
      includeArchived: false,
      isImportDialogVisible: false,
      selectedFile: null,
      isFileUploading: false,
      uploadPercent: 0,
      uploadStatus: '',
      progressBarStatus: '',
      isResultsDialogVisible: false,
      importResults: null,
    };
  },
  props: {
    type: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    storeNamespace: {
      type: String,
      default: '/settings/',
    },
    isSystemKeyword: {
      type: Function,
      required: false,
      default: (_) => false,
    },
    minimumAllowedKewords: {
      type: Number,
      required: false,
      default: (_) => 1,
    },
  },
  mounted() {
    this.pageResource = `${this.storeNamespace}${this.title.toLowerCase()}`;
    this.tempItem = { ...this.$store.getters[this.modelPath] };
    this.checkScrollbar();
  },
  updated() {
    this.checkScrollbar();
  },
  beforeUnmount() {
    clearTimeout(this.scrollbarTimeout);
  },
  computed: {
    ...mapState({
      includeTagsExportImport: state => state.featureFlags.FEATURE_FLAGS.TAGS_EXPORT_IMPORT,
    }),
    selected() {
      return this.$store.state[this.titleLowerCased].selected;
    },
    loaded() {
      return this.$store.state[this.titleLowerCased][`${this.titleLowerCased}Loaded`];
    },
    items() {
      return this.$store.getters[`${this.titleLowerCased}/${this.titleLowerCased}`];
    },
    modelPath() {
      return `${this.titleLowerCased}/${this.type}Model`;
    },
    getPath() {
      return `${this.titleLowerCased}/get${this.title}List`;
    },
    createPath() {
      return `${this.titleLowerCased}/create${this.typeText}`;
    },
    deletePath() {
      return `${this.titleLowerCased}/delete${this.typeText}`;
    },
    updatePath() {
      return `${this.titleLowerCased}/update${this.typeText}`;
    },
    typeText() {
      return this.type.charAt(0).toUpperCase() + this.type.slice(1);
    },
    titleLowerCased() {
      return this.title.toLowerCase();
    },
    exportTranslationPath() {
      return 'settings_tab.keyword_management.export';
    },
    importTranslationPath() {
      return 'settings_tab.keyword_management.import';
    },
    descriptionTranslationPath() {
      return 'settings_tab.keyword_management.description';
    },
  },
  watch: {
    type() {
      this.fetchData();
    },
  },
  methods: {
    async addItem(newItem) {
      const preparedItem = prepareItem(newItem);
      try {
        await this.$store.dispatch(this.createPath, [preparedItem]);
        this.tempItem = { ...this.$store.getters[this.modelPath] };
        this.addItemInput = false;
        this.$aiq.notify.success(this.$t('settings_tab.keyword_management.add_success_msg', { name: preparedItem.name, item: this.titleLowerCased }));
      } catch (error) {
        const message = extractErrorMessage(error) || this.$t('settings_tab.keyword_management.add_failure_msg', { name: preparedItem.name, item: this.titleLowerCased });
        switch (error.status) {
          case UNIQUE_CONSTRAINT_CODE:
            this.$aiq.notify.error(error.response.data.error);
            break;
          case NO_KEYWORD_CODE:
            this.$aiq.notify.error(this.$t('settings_tab.keyword_management.no_keyword_error'));
            break;
          default:
            this.$aiq.notify.error(message);
        }
      }
    },
    addKeywordToItem([item, keyword]) {
      const keywords = uniq([...item.keywords, keyword]);
      this.updateKeywords(item, keywords)
        .then(() => {
          this.$aiq.notify.success(`${keyword} has been added to ${item.name}.`);
        })
        .catch(() => this.$aiq.notify.error(`Error adding ${keyword} to ${item.name}.`));
    },
    /**
     * Check if the scrollbar is visible and if it is visible
     * change actions title column width to be same as the actions column
     */
    checkScrollbar() {
      this.scrollbarTimeout = setTimeout(() => {
        if (!this.$refs['list-content']) {
          return;
        }

        const { offsetHeight: height = {}, scrollHeight: scroll = {} } = this.$refs['list-content'];
        this.scrollbar = height === scroll;
      }, CHECK_SCROLLBAR_TIMEOUT_MS);
    },
    deleteItem(item) {
      const { name } = item;
      this.$aiq.confirm(
        'Archive?',
        `Are you sure you want to archive <b>${name}</b>? You will need to contact support to unarchive.`,
      ).then(() => {
        this.$store.dispatch(this.deletePath, item)
          .then(() => {
            this.$aiq.notify.success(`${name} has been archived.`);
          });
      }).catch((err) => {
        if (err !== 'cancel') {
          this.$aiq.notify.error('Error archiving.', err);
        }
      });
    },
    deleteKeywordFromItem([item, keyword]) {
      const keywords = item.keywords.filter(k => k !== keyword);

      if (keywords.length < this.minimumAllowedKewords) {
        return this.$aiq.notify.error(`${this.typeText} must have at least ${this.minimumAllowedKewords} keyword.`);
      }

      this.updateKeywords(item, keywords)
        .then(() => {
          this.$aiq.notify.success(`${keyword} has been deleted from ${item.name}.`);
        })
        .catch(() => this.$aiq.notify.error(`Error deleting ${keyword} from ${item.name}.`));
    },
    async loadList({ searchTerm = '', searchOffset = 0 }) {
      const params = {
        limit: this.searchPageSize,
        query: searchTerm,
        offset: searchOffset,
      };

      const response = await this.$store.dispatch(this.getPath, [
        params,
        searchOffset === 0,
      ]);
      this.pagination = get(response, 'data.pagination', PAGINATION_DEFAULT);
      return response;
    },
    updateItem(item = this.selected) {
      this.$store.dispatch(this.updatePath, [prepareItem(item), 'data'])
        .then(() => {
          this.$aiq.notify.success(`${this.typeText} has been updated`);
        })
        .catch((error) => this.$aiq.notify.error(extractErrorMessage(error) || `Creation failed. ${item.name} may already exist.`));
    },
    toggleCreateItem(value = !this.addItemInput) {
      this.addItemInput = value;
      if (value) {
        this.$nextTick(() => {
          document.querySelector('#new-item-with-keywords').focus();
        });
      }

      this.checkScrollbar();
    },
    updateKeywords(item, keywords) {
      return this.$store.dispatch(`${this.titleLowerCased}/updateKeywords`, [item, keywords, this.type]);
    },
    fetchData() {
      this.addItemInput = false;
      this.loadList({});
    },
    async exportTags() {
      try {
        const response = await this.$store.dispatch(`${this.titleLowerCased}/export`, !this.includeArchived);
        const fileName = 'tags';
        const fileExtension = '.csv';
        downloadDataAsFile(response, fileName, fileExtension);
        this.$aiq.notify.success(this.$t(`${this.exportTranslationPath}.success_msg`));
        this.isExportDialogVisible = false;
        this.addItemInput = false;
      } catch (_) {
        this.$aiq.notify.error(this.$t(`${this.exportTranslationPath}.error_msg`));
      }
    },
    onUploadChange(data) {
      if (get(data, 'raw.type') === 'text/csv') {
        const [title, ext] = fileNameParser(data.name);
        this.selectedFile = {
          file: data,
          name: data.name,
          title,
          ext,
        };
      } else {
        this.$aiq.notify.error(this.$t(`${this.importTranslationPath}.failure_msgs.file_type`));
      }
    },
    updateProgress(evt) {
      const full = 100;
      this.uploadPercent = parseInt(Math.round((evt.loaded / evt.total) * full));
      if (this.uploadPercent < full) {
        this.uploadStatus = this.$t(`${this.importTranslationPath}.upload_status.in_progress`);
      } else {
        this.uploadStatus = this.$t(`${this.importTranslationPath}.upload_status.completed`);
        this.progressBarStatus = 'success';
      }
    },
    async importTags() {
      try {
        const formData = new FormData();
        formData.append('file', this.selectedFile.file.raw);
        this.isFileUploading = true;
        const importResults = await this.$store.dispatch('tags/import', [formData, this.updateProgress]);
        // Need to wait till progress events render the progress bar fully in small size upload files
        // this.$nextTick does work so we have to use setTimeout
        setTimeout(() => {
          const hasErrors = !!importResults.summary.errors;
          const hasWarnings = !!importResults.summary.warnings;
          if (hasErrors || hasWarnings) {
            this.importResults = importResults;
            this.isResultsDialogVisible = true;
            if (hasErrors) {
              this.$aiq.notify.error(this.$t(`${this.importTranslationPath}.failure_msgs.results_error`));
            } else if (hasWarnings) {
              this.$aiq.notify.info(this.$t(`${this.importTranslationPath}.warning_msg`));
              this.fetchData();
            }
          } else {
            this.$aiq.notify.success(this.$t(`${this.importTranslationPath}.success_msg`));
            this.fetchData();
          }
          this.resetImport();
        }, PROGRESS_BAR_WAIT_MS);
      } catch (error) {
        this.$aiq.notify.error(this.$t(`${this.importTranslationPath}.failure_msgs.generic`));
      }
    },
    resetImport() {
      this.isImportDialogVisible = false;
      this.selectedFile = null;
      this.isFileUploading = false;
      this.uploadStatus = '';
      this.progressBarStatus = '';
    },
    handleBeforeImportClose() {
      if (!this.selectedFile) {
        this.resetImport();
        return;
      }
      confirmBeforeDialogClose(this, this.resetImport);
    },
    resetResults() {
      this.isResultsDialogVisible = false;
      this.importResults = null;
    },
  },
};
